// @mui
import { alpha, styled } from "@mui/material/styles";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemButton from "@mui/material/ListItemButton";

// ----------------------------------------------------------------------

export const StyledItem = styled(ListItemButton, {
  shouldForwardProp: prop => prop !== "active",
})(({ active, depth, config, theme }) => {
  const subItem = depth !== 1;

  const deepSubItem = depth > 2;

  // `contrast`: el nav vive sobre la superficie oscura de marca (sidebar),
  // así que los colores no siguen el modo del tema sino el fondo negro.
  const contrast = !!config.contrast;

  const activeStyles = {
    root: {
      color:
        theme.palette.mode === "light" ? theme.palette.primary.main : theme.palette.primary.light,
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.16),
      },
    },
    sub: {
      color: theme.palette.text.primary,
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
  };

  // Branding 2026: item activo como pastilla púrpura sólida sobre el negro.
  const contrastStyles = {
    base: {
      color: alpha(theme.palette.common.white, 0.72),
      "&:hover": {
        color: theme.palette.common.white,
        backgroundColor: alpha(theme.palette.common.white, 0.08),
      },
    },
    root: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.dark,
      },
    },
    sub: {
      color: theme.palette.common.white,
      backgroundColor: alpha(theme.palette.primary.main, 0.24),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.32),
      },
    },
  };

  const styles = contrast ? contrastStyles : activeStyles;

  return {
    // Root item
    padding: config.itemPadding,
    marginBottom: config.itemGap,
    borderRadius: config.itemRadius,
    minHeight: config.itemRootHeight,
    color: theme.palette.text.secondary,

    ...(contrast && contrastStyles.base),

    // Active root item
    ...(active && {
      ...styles.root,
    }),

    // Sub item
    ...(subItem && {
      minHeight: config.itemSubHeight,
      // Active sub item
      ...(active && {
        ...styles.sub,
      }),
    }),

    // Deep sub item
    ...(deepSubItem && {
      paddingLeft: theme.spacing(depth),
    }),
  };
});

// ----------------------------------------------------------------------

export const StyledIcon = styled(ListItemIcon)(({ size }) => ({
  width: size,
  height: size,
  alignItems: "center",
  justifyContent: "center",
}));

export const StyledDotIcon = styled("span", {
  shouldForwardProp: prop => prop !== "active" && prop !== "contrast",
})(({ active, contrast, theme }) => ({
  width: 4,
  height: 4,
  borderRadius: "50%",
  backgroundColor: contrast ? alpha(theme.palette.common.white, 0.48) : theme.palette.text.disabled,
  transition: theme.transitions.create(["transform"], {
    duration: theme.transitions.duration.shorter,
  }),
  ...(active && {
    transform: "scale(2)",
    backgroundColor: contrast ? theme.palette.common.white : theme.palette.primary.main,
  }),
}));

// ----------------------------------------------------------------------

export const StyledSubheader = styled(ListSubheader)(({ config, theme }) => ({
  ...theme.typography.overline,
  fontSize: 11,
  cursor: "pointer",
  display: "inline-flex",
  padding: config.itemPadding,
  paddingTop: theme.spacing(2),
  marginBottom: config.itemGap,
  paddingBottom: theme.spacing(1),
  color: config.contrast ? alpha(theme.palette.common.white, 0.48) : theme.palette.text.disabled,
  transition: theme.transitions.create(["color"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:hover": {
    color: config.contrast ? theme.palette.common.white : theme.palette.text.primary,
  },
}));
